import type { ForwardRefRenderFunction, RefObject } from "react";
import { useImperativeHandle, useRef } from "react";

type UpstreamRef<T> = Parameters<ForwardRefRenderFunction<T>>[1];

/**
 * Used to simultaneously forward and access a ref in the same component.
 * For example:
 *
 * ```tsx
 * const MyComponent = React.forwardRef((props, ref) => {
 * 	const innerRef = useForwardedRef<HTMLDivElement>(ref);
 *
 * 	innerRef.current; // The value of the ref is accessible here
 * 	return <div ref={innerRef} />; // We pass this along to the child
 * });
 * ```
 *
 * @note
 * This kinda feels too easy? But it seems to work. ¯\\\_(ツ)_/¯
 */
export default function useForwardedRef<T>(
	/** The `ref` argument passed into the ref forwarding function */
	upstreamRef: UpstreamRef<T>,
): RefObject<T> {
	const accessMe = useRef<T>(null);
	useImperativeHandle<T | null, T | null>(upstreamRef, () => accessMe.current, [accessMe]);

	return accessMe;
}
